import moment from 'moment';
import React, { useState } from 'react';
import EmptyState from './EmptyState';
import { useTranslation } from '../i18n';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import AchievementsCard from './AchievementsCard';
import { analyzeReadingPatterns } from '../utils/functions';
import SelectInput from './SelectInput';
import optionsIcon from './../images/calendar-line.svg';

const UserStats = ({ books, userData, theme }) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState('all');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(moment());
  const [singleDay, setSingleDay] = useState(false);
  const haveRead = books.filter((book) => book.status === 'read').length > 0;
  const currentStreak = userData?.streak ? userData.streak.currentStreak : 0;
  const lastStreakDate = userData?.streak?.lastStreakDate;

  const streakDate = lastStreakDate
    ? moment(lastStreakDate).format('YYYY-MM-DD')
    : null;
  const today = moment().format('YYYY-MM-DD');
  const yesterday = moment().subtract(1, 'days').format('YYYY-MM-DD');

  const handleOptionsChange = (e) => {
    const selectedOption = e.target.value;
    setOptions(selectedOption);

    const now = moment();
    let startDate = null;
    let endDate = null;
    let singleDay = false;

    switch (selectedOption) {
      case 'today':
        startDate = now.startOf('day');
        endDate = startDate.clone().endOf('day');
        singleDay = true;
        break;
      case 'yesterday':
        startDate = now.subtract(1, 'days').startOf('day');
        endDate = startDate.clone().endOf('day');
        singleDay = true;
        break;
      case 'thisWeek':
        startDate = now.startOf('week');
        endDate = startDate.clone().endOf('week');
        break;
      case 'lastWeek':
        startDate = now.subtract(1, 'week').startOf('week');
        endDate = startDate.clone().endOf('week');
        break;
      case 'thisMonth':
        startDate = now.startOf('month');
        endDate = startDate.clone().endOf('month');
        break;
      case 'lastMonth':
        startDate = now.subtract(1, 'month').startOf('month');
        endDate = startDate.clone().endOf('month');
        break;
      case 'thisYear':
        startDate = now.startOf('year');
        endDate = startDate.clone().endOf('year');
        break;
      case 'lastYear':
        startDate = now.subtract(1, 'year').startOf('year');
        endDate = startDate.clone().endOf('year');
        break;
      case 'all':
      default:
        startDate = null;
        endDate = now;
        singleDay = false;
        break;
    }

    setStartDate(startDate);
    setEndDate(endDate);
    setSingleDay(singleDay);
  };

  function filterSessionsByDateRange(data) {
    return data?.filter((item) => {
      if (!item.date) return false; // Skip items without a date

      // Convert Firestore Timestamp to JavaScript Date
      const itemDate = moment(
        new Date(item.date.seconds * 1000 + item.date.nanoseconds / 1e6),
      );

      // Check if itemDate is between startDate and endDate (inclusive)
      return itemDate.isBetween(startDate, endDate, 'day', '[]');
    });
  }

  const streakMoment = moment(streakDate, moment.ISO_8601, true); // Strict parsing
  const yesterdayMoment = moment(yesterday, moment.ISO_8601, true);
  const todayMoment = moment(today, moment.ISO_8601, true);

  const streak =
    streakMoment.isValid() &&
    (streakMoment.isSame(yesterdayMoment, 'day') ||
      streakMoment.isSame(todayMoment, 'day'))
      ? currentStreak
      : 0;

  function calculateReadingStats(books) {
    let totalBooksRead = 0;
    let totalBooksReadWithDate = 0;
    let totalPagesRead = 0;
    let totalSessions = 0;
    let totalSessionTime = 0;
    let totalSessionPages = 0;
    let totalDurationInSeconds = 0;
    let totalBooksReadWithSession = 0;
    let sessionsToFinishBook = 0;
    let pagesPerReadBook = 0;
    let timeReadPerBook = 0;
    let booksReadWithSessionTime = 0;
    let averageReadingDuration = 0;

    if (!books) {
      return {
        totalBooksRead,
        totalPagesRead,
        totalSessions,
        totalSessionTime,
        totalSessionPages,
        averageReadingDuration,
      };
    }

    books.forEach((book) => {
      const bookDateFinished = moment(
        book.dateFinished
          ? typeof book.dateFinished.toDate === 'function'
            ? book.dateFinished.toDate() // Firestore Timestamp
            : book.dateFinished // Plain Date object or ISO string
          : null, // Default value if undefined
      );

      const readBetween = startDate
        ? book.status === 'read' &&
          bookDateFinished.isBetween(startDate, endDate, 'day', '[]')
        : book.status === 'read';

      if (readBetween) {
        totalBooksRead += 1;
        if (book.sessions) {
          totalBooksReadWithSession += 1;
          sessionsToFinishBook += book.sessions.length;
          pagesPerReadBook += book.number_of_pages;
          timeReadPerBook += book.sessions.reduce(
            (sum, item) => sum + (item.duration || 0),
            0,
          );
        } else {
          totalPagesRead += parseInt(book.number_of_pages);
        }
      }

      const filteredSessions = startDate
        ? filterSessionsByDateRange(book.sessions)
        : book.sessions;

      if (filteredSessions) {
        totalSessions += parseInt(filteredSessions.length);
        filteredSessions.map((session) => {
          totalSessionTime += parseInt(session.duration);
          totalSessionPages += parseInt(session.pages);
          totalPagesRead += parseInt(session.pages);
          if (book.status === 'read') {
            booksReadWithSessionTime += parseInt(session.duration);
          }
        });
      }

      const bookFinishedWithinPeriod = startDate
        ? book.status === 'read' &&
          book.dateStarted &&
          book.dateFinished &&
          moment(book.dateStarted.toDate()).isAfter(startDate) &&
          moment(book.dateFinished.toDate()).isBefore(endDate)
        : book.status === 'read' && book.dateStarted && book.dateFinished;

      if (bookFinishedWithinPeriod) {
        totalBooksReadWithDate += 1;
        const start = moment(book.dateStarted.toDate());
        const end = moment(book.dateFinished.toDate());
        totalDurationInSeconds += end.diff(start, 'seconds');
      }
    });

    averageReadingDuration =
      totalBooksReadWithDate > 0
        ? totalDurationInSeconds / totalBooksReadWithDate
        : 0;

    return {
      totalBooksRead,
      totalPagesRead,
      totalSessions,
      totalSessionTime,
      totalSessionPages,
      averageReadingDuration,
      totalBooksReadWithSession,
      sessionsToFinishBook,
      pagesPerReadBook,
      timeReadPerBook,
      booksReadWithSessionTime,
    };
  }

  function userStatSecondsToTime(seconds, detailed = false) {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds - minutes * 60;

    return [
      days > 0 && (
        <div key="days">
          {days.toLocaleString()} {days === 1 ? t.d : t.ds}
        </div>
      ),
      hours > 0 && (
        <div key="hours">
          {hours} {t.h}
        </div>
      ),
      days === 0 && minutes > 0 && (
        <div key="minutes">
          {minutes} {t.min} {detailed && `${secs} ${t.sec}`}
        </div>
      ),
      days === 0 && hours === 0 && minutes === 0 && (
        <div key="seconds">
          {seconds} {t.sec}
        </div>
      ),
    ];
  }

  const readingStats = calculateReadingStats(books);
  const readingPatterns = userData?.sessions
    ? analyzeReadingPatterns(
        startDate
          ? filterSessionsByDateRange(userData.sessions)
          : userData.sessions,
      )
    : {};

  return (
    <div className="section">
      <Tabs>
        <TabList className="tabs">
          <Tab className="tab fs-l">{t.stats}</Tab>
          <Tab className="tab fs-l">{t.achievements}</Tab>
        </TabList>

        <TabPanel>
          {books.length > 0 ? (
            <div className="card-wrapper">
              <div className="form">
                <SelectInput
                  labelName={t.timeperiod}
                  id="selectRange"
                  onChangeFunction={handleOptionsChange}
                  value={options}
                  icon={optionsIcon}
                  minimal={true}
                  options={[
                    { value: 'all', label: t.alltime },
                    { value: 'today', label: t.today },
                    { value: 'yesterday', label: t.yesterday },
                    { value: 'thisWeek', label: t.thisweek },
                    { value: 'lastWeek', label: t.lastweek },
                    { value: 'thisMonth', label: t.thismonth },
                    { value: 'lastMonth', label: t.lastmonth },
                    { value: 'thisYear', label: t.thisyear },
                    { value: 'lastYear', label: t.lastyear },
                  ]}
                />
              </div>

              <div className="card">
                <div className="card-justified">
                  <div className="user-stat-wrapper">
                    <div className="user-stat">
                      <div>{readingStats.totalSessions.toLocaleString()}</div>
                    </div>
                    <div className="fs-s pt-65">{t.statTotalSessions}</div>
                  </div>

                  <div className="user-stat-wrapper">
                    <div className="user-stat">
                      <div>
                        {readingStats.totalSessions > 0
                          ? userStatSecondsToTime(
                              Math.round(
                                readingStats.totalSessionTime /
                                  readingStats.totalSessions,
                              ),
                            )
                          : '—'}
                      </div>
                    </div>
                    <div className="fs-s pt-65">{t.statAvgTimePerSession}</div>
                  </div>

                  <div className="user-stat-wrapper">
                    <div className="user-stat">
                      <div>
                        {readingStats.totalSessions > 0
                          ? (
                              Math.round(
                                (10 * readingStats.totalSessionPages) /
                                  readingStats.totalSessions,
                              ) / 10
                            ).toLocaleString()
                          : '—'}
                      </div>
                    </div>
                    <div className="fs-s pt-65">{t.statAvgPagePerSession}</div>
                  </div>

                  <div className="user-stat-wrapper">
                    <div className="user-stat">
                      <div>{(streak || 0).toLocaleString()}</div>
                    </div>
                    <div className="fs-s pt-65">{t.statCurrentStreak}</div>
                  </div>
                </div>

                <div className="separator"></div>

                <div className="read-book-row">
                  <div className="fs-s pt-65">{t.statTotalSessionTime}</div>
                  <div className="pt-65 flex-grow align-right nowrap read-book-row">
                    {userStatSecondsToTime(
                      Math.round(readingStats.totalSessionTime),
                    )}
                  </div>
                </div>

                <div className="read-book-row">
                  <div className="fs-s pt-65">{t.statTotalSessionPages}</div>
                  <div className="pt-65 flex-grow align-right nowrap read-book-row">
                    {Math.round(
                      readingStats.totalSessionPages,
                    ).toLocaleString()}{' '}
                    {t.pagesLow}
                  </div>
                </div>

                <div className="read-book-row">
                  <div className="fs-s pt-65">{t.statAvgTimePerPages}</div>
                  <div className="pt-65 flex-grow align-right nowrap read-book-row">
                    {readingStats.totalSessions > 0
                      ? userStatSecondsToTime(
                          Math.round(
                            readingStats.totalSessionTime /
                              readingStats.totalSessionPages,
                          ),
                          true,
                        )
                      : '—'}
                  </div>
                </div>

                <div className="read-book-row">
                  <div className="fs-s pt-65">{t.statAvgPagesPerHour}</div>
                  <div className="pt-65 flex-grow align-right nowrap read-book-row">
                    {readingStats.totalSessions > 0
                      ? `${Math.round(
                          (readingStats.totalSessionPages /
                            readingStats.totalSessionTime) *
                            60 *
                            60,
                        )} ${t.pagesLow}`
                      : '—'}
                  </div>
                </div>

                {haveRead && <div className="separator"></div>}

                {haveRead && (
                  <div className="read-book-row">
                    <div className="fs-s pt-65">{t.statTotalBooks}</div>
                    <div className="pt-65 flex-grow align-right nowrap read-book-row">
                      {readingStats.totalBooksRead.toLocaleString()}
                    </div>
                  </div>
                )}

                {haveRead && (
                  <div className="read-book-row">
                    <div className="fs-s pt-65">{t.statAvgSessionsPerBook}</div>
                    <div className="pt-65 flex-grow align-right nowrap read-book-row">
                      {readingStats.totalBooksReadWithSession > 0
                        ? Math.round(
                            readingStats.sessionsToFinishBook /
                              readingStats.totalBooksReadWithSession,
                          ).toLocaleString()
                        : '—'}
                    </div>
                  </div>
                )}

                {haveRead && (
                  <div className="read-book-row">
                    <div className="fs-s pt-65">{t.statPagesPerBook}</div>
                    <div className="pt-65 flex-grow align-right nowrap read-book-row">
                      {readingStats.totalBooksReadWithSession > 0
                        ? Math.round(
                            readingStats.pagesPerReadBook /
                              readingStats.totalBooksReadWithSession,
                          ).toLocaleString()
                        : '—'}
                    </div>
                  </div>
                )}

                {haveRead && (
                  <div className="read-book-row">
                    <div className="fs-s pt-65">{t.statAvgBookTime}</div>
                    <div className="pt-65 flex-grow align-right nowrap read-book-row">
                      {readingStats.totalBooksReadWithSession > 0
                        ? userStatSecondsToTime(
                            Math.round(
                              readingStats.timeReadPerBook /
                                readingStats.totalBooksReadWithSession,
                            ),
                          )
                        : '—'}
                    </div>
                  </div>
                )}

                {haveRead && (
                  <div className="read-book-row">
                    <div className="fs-s pt-65">{t.statAvgBookDuration}</div>
                    <div className="pt-65 flex-grow align-right nowrap read-book-row">
                      {readingStats.averageReadingDuration > 0
                        ? userStatSecondsToTime(
                            Math.round(readingStats.averageReadingDuration),
                          )
                        : '—'}
                    </div>
                  </div>
                )}
              </div>

              {readingStats.totalSessions > 0 && (
                <div className="card">
                  <h3 className="fs-m pt-65">{t.sessionpattern}</h3>
                  <div className="flex-row">
                    <div
                      className="patterns morning"
                      style={{
                        flex: readingPatterns?.timeCategories?.morning,
                      }}
                    >
                      <div>
                        {Math.round(
                          (100 * readingPatterns?.timeCategories?.morning) /
                            readingPatterns?.totalSessions,
                        )}
                        {'%'}
                      </div>
                      <div className="fs-s pt-65">{t.morning}</div>
                    </div>
                    <div
                      className="patterns afternoon"
                      style={{
                        flex: readingPatterns?.timeCategories?.afternoon,
                      }}
                    >
                      <div>
                        {Math.round(
                          (100 * readingPatterns?.timeCategories?.afternoon) /
                            readingPatterns?.totalSessions,
                        )}
                        {'%'}
                      </div>
                      <div className="fs-s pt-65">{t.afternoon}</div>
                    </div>
                    <div
                      className="patterns evening"
                      style={{
                        flex: readingPatterns?.timeCategories?.evening,
                      }}
                    >
                      <div>
                        {Math.round(
                          (100 * readingPatterns?.timeCategories?.evening) /
                            readingPatterns?.totalSessions,
                        )}
                        {'%'}
                      </div>
                      <div className="fs-s pt-65">{t.evening}</div>
                    </div>
                    <div
                      className="patterns night"
                      style={{
                        flex: readingPatterns?.timeCategories?.night,
                      }}
                    >
                      <div>
                        {Math.round(
                          (100 * readingPatterns?.timeCategories?.night) /
                            readingPatterns?.totalSessions,
                        )}
                        {'%'}
                      </div>
                      <div className="fs-s pt-65">{t.night}</div>
                    </div>
                  </div>
                  {!singleDay && (
                    <div className="flex-row">
                      <div
                        className="patterns weekdays"
                        style={{
                          flex: readingPatterns?.dayCategories?.weekdays,
                        }}
                      >
                        <div>
                          {Math.round(
                            (100 * readingPatterns?.dayCategories?.weekdays) /
                              readingPatterns?.totalSessions,
                          )}
                          {'%'}
                        </div>
                        <div className="fs-s">{t.weekdays}</div>
                      </div>
                      <div
                        className="patterns weekends"
                        style={{
                          flex: readingPatterns?.dayCategories?.weekends,
                        }}
                      >
                        <div>
                          {Math.round(
                            (100 * readingPatterns?.dayCategories?.weekends) /
                              readingPatterns?.totalSessions,
                          )}
                          {'%'}
                        </div>
                        <div className="fs-s">{t.weekends}</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <EmptyState state="stats" />
          )}
        </TabPanel>
        <TabPanel>
          <AchievementsCard userData={userData} theme={theme} />
        </TabPanel>
      </Tabs>
    </div>
  );
};

export default UserStats;
